import {
    MATCH_TYPE_EXACT_IGNORE_CASE,
    MATCH_TYPE_WILDCARD,
    type SimpleAndFilter,
} from "@impero-com/common/utils/indexing";
import moment from "moment-timezone";
import { z } from "zod";

import { WebRoute } from "routes";

export const EVENT_LOG_PARAM = {
    START_DATE: "startDate",
    END_DATE: "endDate",
    FILTER: "filter",
} as const;

export const EVENT_LOG_COLUMN = {
    ID: "id",
    TYPE: "type",
} as const;

const DATE_FORMAT = moment.HTML5_FMT.DATETIME_LOCAL_SECONDS;

export function parseEventLogDate(param: string) {
    return moment(param, DATE_FORMAT);
}

const fieldFilterSchema = z.object({
    value: z.unknown(),
    matchType: z.enum([MATCH_TYPE_EXACT_IGNORE_CASE, MATCH_TYPE_WILDCARD]),
});

const filterSchema = z.object({
    filters: z.array(
        z.object({
            extractorId: z.string(),
            filters: z.array(fieldFilterSchema).optional(),
            exclusionFilters: z.array(fieldFilterSchema).optional(),
        })
    ),
}) satisfies z.ZodType<SimpleAndFilter>;

export function parseEventLogFilter(
    param: string | null
): SimpleAndFilter | null {
    if (!param) {
        return null;
    }
    try {
        return filterSchema.safeParse(JSON.parse(param)).data ?? null;
    } catch {
        return null;
    }
}

interface EventLogLink {
    range?: { from: moment.Moment; to: moment.Moment };
    column: (typeof EVENT_LOG_COLUMN)[keyof typeof EVENT_LOG_COLUMN];
    value: string;
}

export function eventLogUrl({ range, column, value }: EventLogLink) {
    const filter = {
        filters: [
            {
                extractorId: column,
                filters: [{ value, matchType: MATCH_TYPE_EXACT_IGNORE_CASE }],
            },
        ],
    } satisfies SimpleAndFilter;
    const params = new URLSearchParams({
        ...(range && {
            [EVENT_LOG_PARAM.START_DATE]: range.from.format(DATE_FORMAT),
            [EVENT_LOG_PARAM.END_DATE]: range.to.format(DATE_FORMAT),
        }),
        [EVENT_LOG_PARAM.FILTER]: JSON.stringify(filter),
    });

    return `${WebRoute.EVENT_LOG}?${params}` as const;
}
